/**
 * RevenueCat Web Service
 * Handles subscription management for web using RevenueCat Web SDK
 * Uses RevenueCat's hosted checkout (popup/redirect) for payments
 *
 * @module services/revenuecatService
 */

import { type CustomerInfo, Purchases } from '@revenuecat/purchases-js';
import logger from '../utils/logger';

// =============================================================================
// CONFIGURATION
// =============================================================================

// RevenueCat Web API Key (from RevenueCat Dashboard → Project → API Keys)
const REVENUECAT_WEB_API_KEY = process.env.REACT_APP_REVENUECAT_WEB_API_KEY || '';

// Offering identifiers (must match RevenueCat dashboard)
const OFFERING_DEFAULT = 'default';
const OFFERING_REFILLS = 'credits_offering';

// Entitlement identifiers
const ENTITLEMENT_BASIC = 'basic_access';
const ENTITLEMENT_PLUS = 'plus_access';
const ENTITLEMENT_PREMIUM = 'premium_access';

// Backend API URL
const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// =============================================================================
// TYPES
// =============================================================================

export interface SubscriptionStatus {
  tier: 'free' | 'basic' | 'plus' | 'premium';
  isActive: boolean;
  expiresAt: string | null;
  entitlements: string[];
  productId: string | null;
  store: string | null;
}

export interface CreditBalance {
  credits: {
    subscription: number;
    addon: number;
    total: number;
  };
  daily_text: {
    limit: number | null;
    used: number;
    remaining: number | null;
  };
  features: {
    voice_video: boolean;
    text: boolean;
    text_only_mode: boolean;
    show_subscription_banner: boolean;
  };
  tier: string;
  subscription_status: string;
  /** True if this user received welcome credits — used to show the first-login toast once. */
  welcome_credits_granted?: boolean;
  /** @deprecated Use welcome_credits_granted instead */
  loginCount?: number;
}

export interface SubscriptionTier {
  id: string;
  name: string;
  price: number;
  priceCents: number;
  monthlyLiveAvatarCredits: number;
  dailyTextQuestions: number | null;
  dailyVoiceMinutes: number | null;
  historyDays: number | null;
  features: string[];
}

export interface CreditPack {
  id: string;
  credits: number;
  price: number;
  priceCents: number;
}

/** Returned by the platform-conflict endpoint */
export interface PlatformConflict {
  has_conflict: boolean;
  active_platform?: 'ios' | 'android' | 'web';
  active_platform_label?: string;
  active_tier?: string;
  expires_at?: string | null;
  cancel_instructions?: {
    platform: string;
    steps: string;
  };
}

/** Error type returned by portal endpoint with optional platform info */
export interface PortalError extends Error {
  platform?: string;
}

// =============================================================================
// PLATFORM CONFLICT CHECK
// =============================================================================

/**
 * Check if the user has an active subscription on a different platform.
 * Uses RevenueCat as single source of truth (Stripe is also in RC via webhook).
 *
 * @param authToken - Auth token for the backend call
 * @param requestingPlatform - The platform making the request ('web' | 'ios' | 'android')
 * @returns PlatformConflict object; has_conflict=false means no conflict
 */
export async function checkPlatformConflict(
  authToken: string,
  requestingPlatform: 'web' | 'ios' | 'android',
): Promise<PlatformConflict> {
  try {
    const response = await fetch(
      `${API_URL}/subscriptions/platform-conflict?requesting_platform=${requestingPlatform}`,
      {
        method: 'GET',
        headers: { Authorization: `Bearer ${authToken}` },
      },
    );

    if (!response.ok) {
      // Fail open — don't block the user if the check fails
      logger.warn('RevenueCat', `Platform conflict check failed: ${response.status}`);
      return { has_conflict: false };
    }

    return (await response.json()) as PlatformConflict;
  } catch (error) {
    logger.warn('RevenueCat', 'Platform conflict check error (failing open)', error);
    return { has_conflict: false };
  }
}

// =============================================================================
// INITIALIZATION
// =============================================================================

let purchasesInstance: Purchases | null = null;

/**
 * Initialize RevenueCat Web SDK
 * Call this after user logs in
 */
export async function initializeRevenueCat(userId: string): Promise<void> {
  if (!REVENUECAT_WEB_API_KEY) {
    logger.warn('RevenueCat', 'Web API key not configured');
    return;
  }

  try {
    purchasesInstance = Purchases.configure(REVENUECAT_WEB_API_KEY, userId);
    logger.info('RevenueCat', 'Web SDK initialized for user', { userId });
  } catch (error) {
    logger.error('RevenueCat', 'Failed to initialize', error as Error);
    throw error;
  }
}

/**
 * Check if RevenueCat is initialized
 */
export function isInitialized(): boolean {
  return purchasesInstance !== null;
}

// =============================================================================
// SUBSCRIPTION STATUS
// =============================================================================

/**
 * Get current subscription status from RevenueCat
 */
export async function getSubscriptionStatus(): Promise<SubscriptionStatus> {
  if (!purchasesInstance) {
    logger.warn('RevenueCat', 'Not initialized, returning free tier');
    return getFreeTierStatus();
  }

  try {
    const customerInfo = await purchasesInstance.getCustomerInfo();
    return parseCustomerInfo(customerInfo);
  } catch (error) {
    logger.error('RevenueCat', 'Failed to get subscription status', error as Error);
    return getFreeTierStatus();
  }
}

/**
 * Parse RevenueCat CustomerInfo into our SubscriptionStatus format
 */
function parseCustomerInfo(customerInfo: CustomerInfo): SubscriptionStatus {
  const activeEntitlements = Object.keys(customerInfo.entitlements?.active || {});

  // Determine tier from entitlements (check highest tier first)
  let tier: 'free' | 'basic' | 'plus' | 'premium' = 'free';
  if (activeEntitlements.includes(ENTITLEMENT_PREMIUM)) {
    tier = 'premium';
  } else if (activeEntitlements.includes(ENTITLEMENT_PLUS)) {
    tier = 'plus';
  } else if (activeEntitlements.includes(ENTITLEMENT_BASIC)) {
    tier = 'basic';
  }

  // Get the primary entitlement info
  const primaryEntitlement =
    customerInfo.entitlements?.active?.[ENTITLEMENT_PREMIUM] ||
    customerInfo.entitlements?.active?.[ENTITLEMENT_PLUS] ||
    customerInfo.entitlements?.active?.[ENTITLEMENT_BASIC];

  const isActive = activeEntitlements.length > 0;
  // properties from RevenueCat SDK might be strings or Dates depending on version/context, but types say null | Date | string?
  // Actually types say Date | null per the error.
  const expirationDate = primaryEntitlement?.expirationDate;
  const expiresAt =
    expirationDate instanceof Date
      ? expirationDate.toISOString()
      : typeof expirationDate === 'string'
        ? expirationDate
        : null;
  const productId = primaryEntitlement?.productIdentifier || null;
  const store = primaryEntitlement?.store || null;

  return {
    tier,
    isActive,
    expiresAt,
    entitlements: activeEntitlements,
    productId,
    store,
  };
}

/**
 * Get default free tier status
 */
function getFreeTierStatus(): SubscriptionStatus {
  return {
    tier: 'free',
    isActive: false,
    expiresAt: null,
    entitlements: [],
    productId: null,
    store: null,
  };
}

/**
 * Sync subscription status to backend
 */
export async function syncSubscriptionToBackend(
  status: SubscriptionStatus,
  authToken: string,
): Promise<void> {
  try {
    const response = await fetch(`${API_URL}/subscriptions/sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        entitlements: status.entitlements,
        product_id: status.productId,
        expires_at: status.expiresAt,
        is_active: status.isActive,
        is_trial: false, // Web doesn't have trials typically
        platform: 'web',
        store: status.store,
      }),
    });

    if (!response.ok) {
      throw new Error(`Sync failed: ${response.status}`);
    }

    logger.info('RevenueCat', 'Synced to backend');
  } catch (error) {
    logger.error('RevenueCat', 'Failed to sync to backend', error as Error);
    throw error;
  }
}

/**
 * Check subscription and sync to backend in one call
 */
export async function checkAndSyncSubscription(authToken: string): Promise<SubscriptionStatus> {
  const status = await getSubscriptionStatus();

  try {
    await syncSubscriptionToBackend(status, authToken);
  } catch (error) {
    logger.warn('RevenueCat', 'Sync failed, using local status', error);
  }

  return status;
}

// =============================================================================
// PURCHASES - Using RevenueCat Checkout
// =============================================================================

/**
 * Purchase a subscription package
 * Opens RevenueCat's hosted checkout
 *
 * @param packageId - Package identifier (e.g., 'basic_monthly', 'premium_monthly')
 * @param offeringId - Offering identifier (default: 'default')
 */
export async function purchaseSubscription(
  packageId: string,
  offeringId: string = OFFERING_DEFAULT,
): Promise<SubscriptionStatus> {
  if (!purchasesInstance) {
    throw new Error('RevenueCat not initialized');
  }

  try {
    logger.info('RevenueCat', 'Starting purchase', { packageId, offeringId });

    // Get offerings to find the package
    const offerings = await purchasesInstance.getOfferings();
    const offering = offerings.all[offeringId];

    if (!offering) {
      throw new Error(`Offering not found: ${offeringId}`);
    }

    const pkg = offering.availablePackages.find(
      (p: { identifier: string }) => p.identifier === packageId,
    );

    if (!pkg) {
      throw new Error(`Package not found: ${packageId}`);
    }

    // Purchase the package - this opens RevenueCat checkout
    /* 
    const { customerInfo } = await purchasesInstance.purchase({ rcPackage: pkg });

    logger.info('RevenueCat', 'Purchase successful');
    return parseCustomerInfo(customerInfo);
    */

    throw new Error('RevenueCat web checkout is disabled. Use checkoutStripeSubscription instead.');
  } catch (error: unknown) {
    if ((error as { userCancelled?: boolean })?.userCancelled) {
      logger.info('RevenueCat', 'Purchase cancelled by user');
      throw new Error('PURCHASE_CANCELLED');
    }
    logger.error('RevenueCat', 'Purchase failed', error as Error);
    throw error;
  }
}

/**
 * Purchase a credit pack
 *
 * @param packId - Credit pack identifier (e.g., 'pack_10', 'pack_50', 'pack_100')
 * @param authToken - Auth token for backend sync
 */
export async function purchaseCreditPack(packId: string, _authToken: string): Promise<void> {
  if (!purchasesInstance) {
    throw new Error('RevenueCat not initialized');
  }

  try {
    logger.info('RevenueCat', 'Starting credit pack purchase', { packId });

    // Get offerings to find the credit pack
    const offerings = await purchasesInstance.getOfferings();
    const offering = offerings.all[OFFERING_REFILLS];

    if (!offering) {
      throw new Error(`Refills offering not found`);
    }

    const pkg = offering.availablePackages.find((p) => p.identifier === packId);

    if (!pkg) {
      throw new Error(`Credit pack not found: ${packId}`);
    }

    // Purchase the pack - this opens RevenueCat checkout
    /*
    await purchasesInstance.purchase({ rcPackage: pkg });

    logger.info('RevenueCat', 'Credit pack purchase successful, syncing to backend');

    // Sync addon credits to backend immediately after purchase
    await syncAddonCreditsToBackend(packId, authToken);

    logger.info('RevenueCat', 'Addon credits synced to backend');
    */

    throw new Error('RevenueCat credit checkout is disabled. Use checkoutStripeCredits instead.');
  } catch (error: unknown) {
    if ((error as { userCancelled?: boolean })?.userCancelled) {
      logger.info('RevenueCat', 'Purchase cancelled by user');
      throw new Error('PURCHASE_CANCELLED');
    }
    logger.error('RevenueCat', 'Credit pack purchase failed', error as Error);
    throw error;
  }
}

/**
 * Sync addon credits to backend after successful RevenueCat purchase
 * This tells the backend to grant credits for the purchased pack
 */
export async function syncAddonCreditsToBackend(packId: string, authToken: string): Promise<void> {
  try {
    const response = await fetch(`${API_URL}/credits/sync-addon`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        pack_id: packId,
        platform: 'web',
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Sync failed: ${response.status}`);
    }

    logger.info('RevenueCat', 'Addon credits synced successfully');
  } catch (error) {
    logger.error('RevenueCat', 'Failed to sync addon credits', error as Error);
    throw error;
  }
}

/**
 * One-click addon credit purchase via Stripe direct charge (no checkout popup)
 * Charges saved payment method server-side and updates DynamoDB immediately
 */
export async function purchaseAddonDirect(
  packId: string,
  authToken: string,
): Promise<{
  granted: number;
  credits: { subscription: number; addon: number; total: number };
}> {
  try {
    logger.info('Credits', 'Starting direct addon purchase', { packId });

    const response = await fetch(`${API_URL}/credits/purchase-addon`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ pack_id: packId }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.message || `Purchase failed: ${response.status}`);
    }

    logger.info('Credits', 'Direct addon purchase successful', {
      granted: data.granted,
      total: data.credits?.total,
    });

    return {
      granted: data.granted,
      credits: data.credits,
    };
  } catch (error) {
    logger.error('Credits', 'Direct addon purchase failed', error as Error);
    throw error;
  }
}

// =============================================================================
// STRIPE NATIVE CHECKOUT (Web Fallback)
// =============================================================================

/**
 * Native Stripe Checkout for Subscriptions
 * Bypasses RevenueCat for web users to properly map stripeCustomerId in backend
 */
export async function checkoutStripeSubscription(planId: string, authToken: string): Promise<void> {
  try {
    logger.info('Stripe', 'Starting subscription checkout', { planId });

    // Map tier string (basic/plus/premium)
    const tier = planId.replace('_monthly', '');

    const response = await fetch(`${API_URL}/subscriptions/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ planId: tier }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.message || `Checkout failed: ${response.status}`);
    }

    // Redirect to Stripe Checkout Returns back to /subscription
    if (data.url) {
      window.location.href = data.url;
    } else {
      throw new Error('No checkout URL returned from backend');
    }
  } catch (error) {
    logger.error('Stripe', 'Subscription checkout init failed', error as Error);
    throw error;
  }
}

/**
 * Native Stripe Checkout for Addon Credits
 * Bypasses RevenueCat for web fallback when NO_PAYMENT_METHOD occurs
 */
export async function checkoutStripeCredits(packId: string, authToken: string): Promise<void> {
  try {
    logger.info('Stripe', 'Starting credits checkout', { packId });

    const response = await fetch(`${API_URL}/credits/create-checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ pack_id: packId }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.message || `Checkout failed: ${response.status}`);
    }

    if (data.url) {
      window.location.href = data.url;
    } else {
      throw new Error('No checkout URL returned from backend');
    }
  } catch (error) {
    logger.error('Stripe', 'Credits checkout init failed', error as Error);
    throw error;
  }
}

// =============================================================================
// OFFERINGS & PRODUCTS
// =============================================================================

/**
 * Get available subscription offerings from RevenueCat
 */
export async function getOfferings(): Promise<unknown> {
  if (!purchasesInstance) {
    throw new Error('RevenueCat not initialized');
  }

  try {
    const offerings = await purchasesInstance.getOfferings();
    return offerings;
  } catch (error) {
    logger.error('RevenueCat', 'Failed to get offerings', error as Error);
    throw error;
  }
}

/**
 * Get available tiers from backend
 */
export async function getAvailableTiers(authToken: string): Promise<{
  tiers: SubscriptionTier[];
  creditPacks: CreditPack[];
}> {
  try {
    const response = await fetch(`${API_URL}/subscriptions/tiers`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get tiers: ${response.status}`);
    }

    const data = await response.json();

    return {
      tiers: data.tiers.map(
        (t: {
          id: string;
          name: string;
          price: number;
          price_cents: number;
          monthly_live_avatar_credits: number;
          daily_text_questions: number | null;
          daily_voice_minutes: number | null;
          history_days: number | null;
          features: string[];
        }) => ({
          id: t.id,
          name: t.name,
          price: t.price,
          priceCents: t.price_cents,
          monthlyLiveAvatarCredits: t.monthly_live_avatar_credits,
          dailyTextQuestions: t.daily_text_questions,
          dailyVoiceMinutes: t.daily_voice_minutes,
          historyDays: t.history_days,
          features: t.features,
        }),
      ),
      creditPacks: data.credit_packs.map(
        (p: { id: string; credits: number; price: number; price_cents: number }) => ({
          id: p.id,
          credits: p.credits,
          price: p.price,
          priceCents: p.price_cents,
        }),
      ),
    };
  } catch (error) {
    logger.error('RevenueCat', 'Failed to get tiers', error as Error);
    throw error;
  }
}

// =============================================================================
// CREDITS (from backend)
// =============================================================================

/**
 * Get credit balance from backend
 */
export async function getCreditBalance(authToken: string): Promise<CreditBalance> {
  try {
    const response = await fetch(`${API_URL}/credits/balance`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get balance: ${response.status}`);
    }

    const data = await response.json();

    return {
      credits: {
        subscription: data.credits.subscription,
        addon: data.credits.addon,
        total: data.credits.total,
      },
      daily_text: data.daily_text,
      features: data.features,
      tier: data.tier,
      subscription_status: data.subscription_status,
      welcome_credits_granted: data.welcome_credits_granted ?? false,
    };
  } catch (error) {
    logger.error('RevenueCat', 'Failed to get credit balance', error as Error);
    throw error;
  }
}

/**
 * Validate if user can access a feature
 */
export async function validateFeatureAccess(
  feature: 'live_avatar' | 'voice' | 'text',
  authToken: string,
): Promise<{
  allowed: boolean;
  reason?: string;
  remaining: number | null;
}> {
  try {
    const response = await fetch(`${API_URL}/credits/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ feature }),
    });

    if (!response.ok) {
      throw new Error(`Validation failed: ${response.status}`);
    }

    const data = await response.json();
    return {
      allowed: data.allowed,
      reason: data.reason,
      remaining: data.remaining,
    };
  } catch (error) {
    logger.error('RevenueCat', 'Failed to validate access', error as Error);
    // Fail closed - deny access on error
    return {
      allowed: false,
      reason: 'VALIDATION_ERROR',
      remaining: null,
    };
  }
}

// =============================================================================
// SPONSORSHIP PURCHASE
// =============================================================================

/**
 * Purchase a shaab sponsorship
 * Native Stripe Checkout for Subscriptions - completely bypasses RevenueCat for web
 *
 * @param avatarId - The avatar/shaab being sponsored
 * @param avatarName - Display name of the avatar
 * @param displayName - Sponsor's display name (optional, for public display)
 * @param notes - Sponsor's note (optional, for banner display)
 * @param isPublic - Whether sponsorship should be public
 * @param authToken - Auth token for backend sync
 */
export async function purchaseSponsorship(
  avatarId: string,
  avatarName: string,
  displayName: string | undefined,
  notes: string | undefined,
  isPublic: boolean,
  authToken: string,
): Promise<{ success: boolean; message: string }> {
  try {
    logger.info('Stripe', 'Starting sponsorship checkout', { avatarId, avatarName });

    const response = await fetch(`${API_URL}/avatar/${avatarId}/sponsor/donation/checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        displayName: displayName || null,
        notes: notes || null,
        isPublic,
      }),
    });

    const data = await response.json();

    if (response.status === 409) {
      throw new Error(data.error || data.message || 'This companion has already been sponsored');
    }

    if (!response.ok) {
      throw new Error(data.error || data.message || `Checkout failed: ${response.status}`);
    }

    if (data.url) {
      window.location.href = data.url;
      // Return a dummy object so UI doesn't crash before the redirect finishes
      return { success: false, message: 'Redirecting to secure checkout...' };
    } else {
      throw new Error('No checkout URL returned from backend');
    }
  } catch (error) {
    logger.error('Stripe', 'Sponsorship checkout init failed', error as Error);
    throw error;
  }
}

// =============================================================================
// CUSTOMER PORTAL
// =============================================================================

/**
 * Open customer portal for subscription management
 * Tries RevenueCat management URL first, falls back to Stripe Customer Portal
 * User can cancel, update payment method, view invoices, etc.
 *
 * @param authToken - Auth token for backend API call (needed for Stripe portal fallback)
 */
export async function openCustomerPortal(authToken?: string): Promise<void> {
  try {
    // Try RevenueCat management URL first (for App Store/Play Store subscriptions)
    if (purchasesInstance) {
      const customerInfo = await purchasesInstance.getCustomerInfo();
      const managementUrl = customerInfo.managementURL;

      if (managementUrl) {
        logger.info('RevenueCat', 'Opening RevenueCat management URL');
        window.open(managementUrl, '_blank');
        return;
      }
    }

    // Fallback to Stripe Customer Portal (for web subscriptions)
    logger.info('RevenueCat', 'No RevenueCat URL, trying Stripe portal');

    if (!authToken) {
      throw new Error('Auth token required for Stripe portal');
    }

    const response = await fetch(`${API_URL}/subscriptions/portal`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      const errorData = (await response.json().catch(() => ({}))) as Record<string, unknown>;
      // If it's a known error like MANAGED_ON_MOBILE, use its error key for exact matching
      const errorMessage =
        (errorData.error as string) === 'MANAGED_ON_MOBILE'
          ? 'MANAGED_ON_MOBILE'
          : ((errorData.message as string) ?? `Portal request failed: ${response.status}`);

      const portalError = new Error(errorMessage);

      // Attach platform info for caller to show platform-specific messaging
      (portalError as PortalError).platform = errorData.originalPurchasePlatform as
        | string
        | undefined;
      throw portalError;
    }

    const data = await response.json();

    if (data.portal_url) {
      logger.info('RevenueCat', 'Opening Stripe Customer Portal');
      window.open(data.portal_url, '_blank');
    } else {
      throw new Error('No portal URL returned');
    }
  } catch (error) {
    logger.error('RevenueCat', 'Failed to open customer portal', error as Error);
    throw error;
  }
}

// =============================================================================
// LOGOUT
// =============================================================================

/**
 * Clear RevenueCat user on logout
 * For Web SDK, we just close the instance and set it to null
 */
export async function clearRevenueCatUser(): Promise<void> {
  if (purchasesInstance) {
    try {
      // Web SDK doesn't have logOut(), just close the instance
      purchasesInstance.close();
      purchasesInstance = null;
      logger.info('RevenueCat', 'User logged out');
    } catch (error) {
      logger.error('RevenueCat', 'Failed to log out', error as Error);
      // Even if close() fails, set to null
      purchasesInstance = null;
    }
  }
}
